/**
 * FO-102 — acquisitie-flow-spine: de toegestane status-overgangen + labels/
 * volgorde voor de acquisitie-trechter (lead → relatie), als één gedeelde bron
 * voor de UI én de server-side guard (analoog aan `KANDIDAAT_STATUS_TRANSITIES`
 * voor recruitment).
 *
 * De pipeline-volgorde + labels komen 1:1 uit `targeting.ts` (geen duplicaat).
 * Hier komt enkel de overgangs-matrix + het Zod-request-schema bij. De API
 * valideert dezelfde set die de UI toont, zodat de UI nooit een overgang kan
 * tonen die de server weigert.
 */

import { z } from 'zod'
import {
  ACQUISITIE_STATUS,
  ACQUISITIE_STATUS_LABELS,
  ACQUISITIE_PIPELINE_ORDER,
  type AcquisitieStatus,
} from './targeting'

/**
 * Toegestane handmatige status-overgangen per huidige status. Filosofie:
 *   - stap-voor-stap vooruit/terug door de pipeline (correctie mogelijk),
 *   - `VERLOREN` en `ON_HOLD` zijn vanuit elke actieve stap bereikbaar,
 *   - `GEWONNEN` kan alléén vanuit `ONDERHANDELING` (geen vrije sprong naar
 *     gewonnen zonder de tussenstappen — behalve een expliciete ADMIN-override),
 *   - `ON_HOLD` hervat naar elke actieve stap; `VERLOREN` heropent naar
 *     `PROSPECT`.
 */
export const ACQUISITIE_STATUS_TRANSITIES: Record<AcquisitieStatus, readonly AcquisitieStatus[]> = {
  PROSPECT:          ['EERSTE_CONTACT', 'VERLOREN', 'ON_HOLD'],
  EERSTE_CONTACT:    ['PROSPECT', 'IN_GESPREK', 'VERLOREN', 'ON_HOLD'],
  IN_GESPREK:        ['EERSTE_CONTACT', 'AFSPRAAK_GEPLAND', 'VERLOREN', 'ON_HOLD'],
  AFSPRAAK_GEPLAND:  ['IN_GESPREK', 'OFFERTE_VERSTUURD', 'VERLOREN', 'ON_HOLD'],
  OFFERTE_VERSTUURD: ['AFSPRAAK_GEPLAND', 'ONDERHANDELING', 'VERLOREN', 'ON_HOLD'],
  ONDERHANDELING:    ['OFFERTE_VERSTUURD', 'GEWONNEN', 'VERLOREN', 'ON_HOLD'],
  GEWONNEN:          ['ONDERHANDELING', 'ON_HOLD'],
  VERLOREN:          ['PROSPECT', 'ON_HOLD'],
  ON_HOLD:           ['PROSPECT', 'EERSTE_CONTACT', 'IN_GESPREK', 'AFSPRAAK_GEPLAND', 'OFFERTE_VERSTUURD', 'ONDERHANDELING', 'VERLOREN'],
}

/** De eindstaten (zijsporen) buiten de lineaire pipeline. */
export const ACQUISITIE_ZIJSPOOR_STATUSSEN: readonly AcquisitieStatus[] = ['VERLOREN', 'ON_HOLD']

/** De spine-stappen (pipeline, in volgorde) als {key,label} voor `FlowStatusSpine`. */
export const ACQUISITIE_SPINE_STAPPEN: ReadonlyArray<{ key: AcquisitieStatus; label: string }> =
  ACQUISITIE_PIPELINE_ORDER.map((key) => ({ key, label: ACQUISITIE_STATUS_LABELS[key] }))

/** De zijsporen (eindstaten) als {key,label} voor `FlowStatusSpine`. */
export const ACQUISITIE_SPINE_ZIJSPOREN: ReadonlyArray<{ key: AcquisitieStatus; label: string }> =
  ACQUISITIE_ZIJSPOOR_STATUSSEN.map((key) => ({ key, label: ACQUISITIE_STATUS_LABELS[key] }))

/**
 * Is een handmatige overgang `van` → `naar` toegestaan? Met een ADMIN-override
 * is elke (niet-identieke) overgang toegestaan; anders alleen de set uit
 * {@link ACQUISITIE_STATUS_TRANSITIES}.
 */
export function isAcquisitieStatusTransitionAllowed(
  van: AcquisitieStatus,
  naar: AcquisitieStatus,
  opts: { adminOverride?: boolean } = {},
): boolean {
  if (van === naar) return false
  if (opts.adminOverride) return true
  return (ACQUISITIE_STATUS_TRANSITIES[van] ?? []).includes(naar)
}

/** De toegestane vervolg-statussen vanuit `van` (zonder admin-override). */
export function acquisitieToegestaneOvergangen(van: AcquisitieStatus): readonly AcquisitieStatus[] {
  return ACQUISITIE_STATUS_TRANSITIES[van] ?? []
}

/** Gedeeld request-schema voor `POST /acquisitie/leads/:leadId/status`. */
export const acquisitieStatusOvergangSchema = z.object({
  status: z.enum(ACQUISITIE_STATUS),
  /** Alleen gehonoreerd voor een ADMIN; staat een sprong buiten de matrix toe. */
  adminOverride: z.boolean().optional(),
})
export type AcquisitieStatusOvergangInput = z.infer<typeof acquisitieStatusOvergangSchema>
